import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline, Box, CircularProgress } from '@mui/material';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { AuthProvider, useAuth } from './context/AuthContext';
import Layout from './components/Layout';
import Login from './modules/auth/Login';
import OTPVerification from './modules/auth/OTPVerification';
import MfaVerification from './modules/auth/MfaVerification';
import Dashboard from './modules/dashboard/Dashboard';
import TenantsList from './modules/tenants/TenantsList';
import TenantDetail from './modules/tenants/TenantDetail';
import TenantForm from './modules/tenants/TenantForm';
import TenantOnboardWizard from './modules/tenants/TenantOnboardWizard';
import UsersList from './modules/users/UsersList';
import AccountsList from './modules/accounts/AccountsList';
import AccountDetail from './modules/accounts/AccountDetail';
import ContactsList from './modules/contacts/ContactsList';
import ClientLogin from './modules/client/ClientLogin';
import ClientReview from './modules/client/ClientReview';
import SystemInformation from './modules/settings/SystemInformation';
import SourceSettings from './modules/settings/SourceSettings';
import GeneralSettings from './modules/settings/GeneralSettings';
import RolesSettings from './modules/settings/RolesSettings';
import GroupsSettings from './modules/settings/GroupsSettings';
import MfaSettings from './modules/settings/MfaSettings';
import ChangePasswordRequired from './modules/auth/ChangePasswordRequired';
import DomainSettings from './modules/settings/DomainSettings';
import SoftwareApplicationsSettings from './modules/settings/SoftwareApplicationsSettings';
import ServicesSettings from './modules/settings/ServicesSettings';
import PricingSettings from './modules/settings/PricingSettings';
import PricingMetricsSettings from './modules/settings/PricingMetricsSettings';
import AcrImagesSettings from './modules/settings/AcrImagesSettings';
import InfrastructureSettings from './modules/settings/InfrastructureSettings';
import IpAccessSettings from './modules/settings/IpAccessSettings';
import RepositorySettings from './modules/settings/RepositorySettings';
import ReleaseNotesSettings from './modules/settings/ReleaseNotesSettings';
import TenantSignup from './modules/signup/TenantSignup';
import SignupRequests from './modules/signup/SignupRequests';
import AllTickets from './modules/helpdesk/AllTickets';
import Profile from './modules/profile/Profile';
import IpAccessDenied from './modules/auth/IpAccessDenied';
import { ThemeTemplateProvider } from './context/ThemeTemplateContext';

// Create theme
const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#f59e0b'
    },
    secondary: {
      main: '#ef4444'
    },
    background: {
      default: '#f1f5f9'
    }
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif'
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8
        }
      }
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12
        }
      }
    }
  }
});

// Protected route wrapper
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (user?.must_change_password && location.pathname !== '/change-password-required') {
    return <Navigate to="/change-password-required" replace />;
  }

  return children;
};

// Requires auth but allows password-change screen (blocks rest of app until password updated)
const PasswordChangeRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!user?.must_change_password) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Public route wrapper (redirect if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/verify-mfa"
        element={
          <PublicRoute>
            <MfaVerification />
          </PublicRoute>
        }
      />
      <Route
        path="/verify-otp"
        element={
          <PublicRoute>
            <OTPVerification />
          </PublicRoute>
        }
      />
      <Route
        path="/change-password-required"
        element={
          <PasswordChangeRoute>
            <ChangePasswordRequired />
          </PasswordChangeRoute>
        }
      />
      <Route path="/client/login" element={<ClientLogin />} />
      <Route path="/client/review" element={<ClientReview />} />
      <Route path="/signup" element={<TenantSignup />} />
      <Route path="/access-denied" element={<IpAccessDenied />} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Dashboard />} />
        <Route path="tenants" element={<TenantsList />} />
        <Route path="tenants/onboard" element={<TenantOnboardWizard />} />
        <Route path="tenants/new" element={<TenantForm />} />
        <Route path="tenants/:id" element={<TenantDetail />} />
        <Route path="tenants/:id/edit" element={<TenantForm />} />
        <Route path="signup-requests" element={<SignupRequests />} />
        <Route path="help-desk" element={<Navigate to="/help-desk/tickets" replace />} />
        <Route path="help-desk/tickets" element={<AllTickets />} />
        <Route path="users" element={<UsersList />} />
        <Route path="accounts" element={<AccountsList />} />
        <Route path="accounts/:id" element={<AccountDetail />} />
        <Route path="accounts/:accountId/contacts" element={<ContactsList />} />
        <Route path="contacts" element={<ContactsList />} />
        <Route path="settings/general" element={<GeneralSettings />} />
        <Route path="settings/system-information" element={<SystemInformation />} />
        <Route path="settings/file-location" element={<SourceSettings />} />
        <Route path="settings" element={<Navigate to="/settings/general" replace />} />
        <Route path="settings/source-configuration" element={<SourceSettings />} />
        <Route path="settings/role-permissions" element={<Navigate to="/settings/roles" replace />} />
        <Route path="settings/roles" element={<RolesSettings />} />
        <Route path="settings/groups" element={<GroupsSettings />} />
        <Route path="settings/mfa" element={<MfaSettings />} />
        <Route path="settings/domains" element={<DomainSettings />} />
        <Route path="settings/software-applications" element={<SoftwareApplicationsSettings />} />
        <Route path="settings/services" element={<ServicesSettings />} />
        <Route path="settings/pricing-metrics" element={<PricingMetricsSettings />} />
        <Route path="settings/pricing" element={<PricingSettings />} />
        <Route path="settings/acr-images" element={<AcrImagesSettings />} />
        <Route path="settings/infrastructure" element={<InfrastructureSettings />} />
        <Route path="settings/ip-access" element={<IpAccessSettings />} />
        <Route path="settings/repositories" element={<RepositorySettings />} />
        <Route path="settings/release-notes" element={<ReleaseNotesSettings />} />
        <Route path="profile" element={<Profile />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BrowserRouter>
        <ThemeTemplateProvider>
          <AuthProvider>
            <AppRoutes />
          </AuthProvider>
        </ThemeTemplateProvider>
      </BrowserRouter>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
      />
    </ThemeProvider>
  );
}

export default App;
